// src/oura/merchant/firstRunState.ts
//
// Whether a merchant is still before their first AI check, read off the
// set-up read (FirstRun.tsx draws the pages). Absent status means not known,
// so the normal pages show.

import type { MerchantFirstCheck } from "@service/aeo/onboarding";
import { useMySetup, type MySetupRead } from "../hooks/useMySetup";

export interface FirstRun {
    check: MerchantFirstCheck;
    /** Set-up steps still theirs, and how many there are. */
    left: number;
    total: number;
}

/** The first-run state, or null when the merchant is past it or it is not known. */
export function firstRunOf(read: MySetupRead): FirstRun | null {
    if (read.status !== "ready") return null;
    const check = read.setup.firstCheck;
    if (!check || check.status === "ready") return null;
    return { check, left: read.setup.mine, total: read.setup.done + read.setup.mine };
}

/** `enabled` false skips the read: staff have no set-up of their own. */
export function useFirstRun(enabled = true): FirstRun | null {
    return useFirstRunRead(enabled).run;
}

/** The same, plus whether it is still unknown: a page that first run replaces draws nothing until then (#4440). */
export function useFirstRunRead(enabled = true): { run: FirstRun | null; pending: boolean } {
    const read = useMySetup(enabled);
    return { run: firstRunOf(read), pending: read.status === "loading" };
}
